import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from './EmptyState';
import LoadErrorBox from './ui/LoadErrorBox';
import AccountBar from './account/AccountBar';
import { usePageRefresh } from '../hooks/usePageRefresh';
import ChooseProjectDialog from './account/ChooseProjectDialog';
import { useExport } from './header/exporters';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { roleLevel } from '../utils/roles';
import { getActiveProjectId } from '../utils/session';
import { openInProject } from '../utils/openInProject';
import { errorMessageOf } from '../utils/apiErrors';
import { useNotifications } from './NotificationProvider';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import { entriesInMonth, loadAccountCalendar, type AccountCalendarEntry } from '../services/AccountCalendarService';
import { formatDate, formatMonthYear } from '../utils/formatDate';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { genericMatch } from '../utils/searchMatch';
import '../styles/components/EventsScreens.css';

/** The form that adds an event to the active project. */
const NEW_EVENT_PATH = '/events/new';

const pad = (n: number) => String(n).padStart(2, '0');
const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const shiftMonth = (key: string, by: number): string => {
  const [y, m] = key.split('-').map(Number);
  return monthKey(new Date(y, m - 1 + by, 1));
};
const monthLabel = (key: string): string => {
  const [y, m] = key.split('-').map(Number);
  return formatMonthYear(new Date(y, m - 1, 1));
};

/** Account-level Calendar: the events of every project, by month. */
const AccountCalendarView: React.FC = () => {
  const { workspaces, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const { canEdit } = useRole();
  const { showError } = useNotifications();
  const [choosingProject, setChoosingProject] = useState(false);
  const [startingNew, setStartingNew] = useState(false);
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [entries, setEntries] = useState<AccountCalendarEntry[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const latest = useRef(0);
  const load = useCallback(async () => {
    const mine = ++latest.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadAccountCalendar(workspaces);
      if (mine !== latest.current) return;
      setEntries(result.entries);
      setFailed(result.failed);
      // Every project failing is a failed load, not an empty calendar.
      if (workspaces.length > 0 && result.failed.length === workspaces.length) setLoadError(new Error('None of your projects could be read.'));
    } catch (err) {
      if (mine === latest.current) setLoadError(err);
    } finally {
      if (mine === latest.current) setLoading(false);
    }
  }, [workspaces]);

  useEffect(() => { void load(); return () => { latest.current += 1; }; }, [load]);
  // The header's Refresh reads every project's events again.
  usePageRefresh(load, loading);

  // The header search narrows the month to events whose title, place or project match.
  const search = useHeaderSearch().trim().toLowerCase();
  const inMonth = useMemo(
    () => entriesInMonth(entries, month).filter(e => !search || genericMatch(
      { title: e.event.title, location: e.event.location, time: e.event.time, category: e.event.category, project: e.projectName }, search)),
    [entries, month, search],
  );
  const byDay = useMemo(() => {
    const groups = new Map<string, AccountCalendarEntry[]>();
    inMonth.forEach(e => groups.set(e.day, [...(groups.get(e.day) ?? []), e]));
    return [...groups.entries()];
  }, [inMonth]);

  // Export button: the events of the month on screen.
  useExport('account-calendar', { rows: inMonth, ctx: undefined });

  // An event belongs to a project, so New event asks which one (only projects this person can edit),
  // then opens that project's event form, switching into the project first when it is another one.
  const editableProjects = useMemo(() => workspaces.filter(w => roleLevel(w.role) >= 2), [workspaces]);
  const startNewEvent = async (projectId: string) => {
    if (startingNew) return;
    setStartingNew(true);
    try {
      await openInProject({ target: NEW_EVENT_PATH, projectId, activeProjectId: getActiveProjectId(), navigate, switchWorkspace });
      setChoosingProject(false);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't open that project. Please try again."));
    } finally {
      setStartingNew(false);
    }
  };

  // Month navigation sits in the header bar; the month itself is the trail's last crumb.
  const barActions: BreadcrumbAction[] = [
    { kind: 'button', key: 'prev', icon: 'chevronLeft', title: 'Previous month', onClick: () => setMonth(m => shiftMonth(m, -1)) },
    { kind: 'button', key: 'next', icon: 'chevronRight', title: 'Next month', onClick: () => setMonth(m => shiftMonth(m, 1)) },
    { kind: 'button', key: 'today', icon: 'calendar', label: 'Today', onClick: () => setMonth(monthKey(new Date())) },
    ...(canEdit && editableProjects.length > 0
      ? [{ kind: 'primary' as const, key: 'new', icon: 'plus', label: 'New event', onClick: () => setChoosingProject(true) }]
      : []),
  ];

  return (
    <div className="wt-container fade-in">
      <AccountBar
        view="account-calendar"
        leaf={monthLabel(month)}
        actions={<BreadcrumbBar actions={barActions} />}
        exportKey="account-calendar"
      />

      {failed.length > 0 && (
        <p className="ev-note" role="status">Could not read the events of: {failed.join(', ')}.</p>
      )}

      {loadError ? (
        <LoadErrorBox what="the calendar" error={loadError} onRetry={() => void load()} retrying={loading} />
      ) : byDay.length === 0 ? (
        loading ? null : <EmptyState icon="calendar" title="Nothing on this month." description="Events from all your projects appear here." />
      ) : (
        byDay.map(([day, items]) => (
          <section key={day} className="ev-day-group">
            <h3 className="ev-day-heading">{formatDate(day, day)}</h3>
            <ul className="ev-detail-list">
              {items.map(e => (
                <li key={`${e.projectId}-${e.event.id}`} className="ev-detail-row">
                  <span className="ev-detail-row-title">{e.event.title}</span>
                  <span className="ev-detail-row-meta">
                    {[e.event.time, e.event.location, e.projectName].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <ChooseProjectDialog
        open={choosingProject}
        title="New event"
        what="event"
        projects={editableProjects}
        activeProjectId={getActiveProjectId()}
        busy={startingNew}
        onClose={() => setChoosingProject(false)}
        onContinue={projectId => { void startNewEvent(projectId); }}
      />
    </div>
  );
};

export default AccountCalendarView;
